import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DataGrid } from "@mui/x-data-grid";
import { useGetHWITableQuery } from "../operatorDashboardAPISlice";
import { useParams } from "react-router-dom";
import CustomPagination from "../CustomPagination";
import { readingOrDash } from "../displayValues";

function formatSAST(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const parts = new Intl.DateTimeFormat("en-ZA", {
      timeZone: "Africa/Johannesburg",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const p = Object.fromEntries(parts.map(({ type, value: v }) => [type, v]));
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} SAST`;
  } catch {
    return value;
  }
}

// Missing values arrive as null and show "—"; a real 0 shows 0. Missing came as the text
// "no data", which rendered NaN, and a 0 rendered blank (NEU-1128).
const exponentialOrDash = (value) =>
  value === null || value === undefined ? "—" : Number(value).toExponential(2);

const HWI_CSV_HEADERS = [
  ["hwi_timestamp", "Timestamp (SAST)", (v) => formatSAST(v)],
  ["hot_water_index", "Hot Water Index", (v) => v ?? ""],
  ["heating_gradient", "Heating Gradient", (v) => v ?? ""],
  ["cooling_gradient", "Cooling Gradient", (v) => v ?? ""],
  ["power", "Power (kW)", (v) => v ?? ""],
  ["heating_time", "Heating Time", (v) => v ?? ""],
  ["cooling_time", "Cooling Time", (v) => v ?? ""],
];

function downloadCSV(rows, headers, filename) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const headerRow = headers.map(([, label]) => label).join(",");
  const dataRows = rows.map((row) =>
    headers.map(([key, , transform]) => escape(transform ? transform(row[key], row) : row[key])).join(",")
  );
  const csv = "\uFEFF" + [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const columns = [
  {
    field: "hwi_timestamp",
    headerName: "Timestamp (SAST)",
    flex: 1.5,
    valueFormatter: (params) => formatSAST(params.value),
  },
  { field: "hot_water_index", headerName: "HWI", flex: 1, valueFormatter: (params) => readingOrDash(params.value) },
  {
    field: "heating_gradient",
    headerName: "HG",
    flex: 1,
    valueFormatter: (params) => exponentialOrDash(params.value),
  },
  {
    field: "cooling_gradient",
    headerName: "CG",
    flex: 1,
    valueFormatter: (params) => exponentialOrDash(params.value),
  },
  { field: "power", headerName: "Power (kW)", flex: 1, valueFormatter: (params) => readingOrDash(params.value, 3) },
  {
    field: "heating_time",
    headerName: "Heating Time",
    flex: 1,
    valueFormatter: (params) => exponentialOrDash(params.value),
  },
  {
    field: "cooling_time",
    headerName: "Cooling Time",
    flex: 1,
    valueFormatter: (params) => exponentialOrDash(params.value),
  },
];

const dataGridSx = {
  bgcolor: "transparent",
  color: "#e0e6f0",
  border: "none",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  "& .MuiDataGrid-columnHeaders": {
    bgcolor: "rgba(255,255,255,0.02)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "rgba(255,255,255,0.4)",
    fontWeight: 600,
    fontSize: "0.68rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  "& .MuiDataGrid-cell": { borderColor: "rgba(255,255,255,0.05)", color: "#e0e6f0" },
  "& .MuiDataGrid-row:hover": { bgcolor: "rgba(253,185,64,0.05)" },
  "& .MuiDataGrid-footerContainer": {
    bgcolor: "rgba(255,255,255,0.02)",
    borderTop: "1px solid rgba(255,255,255,0.06)",
    minHeight: "unset",
  },
  "& .MuiDataGrid-sortIcon": { color: "rgba(255,255,255,0.45)" },
  "& .MuiDataGrid-menuIconButton": { color: "rgba(255,255,255,0.45)" },
  "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.08)" },
  "& .MuiDataGrid-overlay": { bgcolor: "#161B22" },
};

export default function TableHWI({ startDate }) {
  const { meter_id } = useParams();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Live mode polls with the charts above it; the table loaded once and went stale (NEU-1128).
  const { data: tableData, isLoading, isError } = useGetHWITableQuery(
    { meter_id, userTimezone, ...(startDate && { start_date: startDate.toISOString() }) },
    { refetchOnMountOrArgChange: true, pollingInterval: startDate ? 0 : 60000 }
  );

  const handleDownload = () => {
    if (!tableData?.length) return;
    const dateTag = startDate
      ? startDate.toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    downloadCSV(tableData, HWI_CSV_HEADERS, `hwi-readings-${meter_id}-${dateTag}.csv`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
        <CircularProgress size={20} sx={{ color: "rgba(255,255,255,0.45)" }} />
        <Typography sx={{ color: "rgba(255,255,255,0.45)" }}>Loading readings…</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: "#e05050" }}>Error retrieving HWI data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#161B22", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden", mb: 4 }}>
      {/* Card header */}
      <Box sx={{ padding: "20px 20px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}>HWI · readings</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>
            Per-minute hot water index for the selected window
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          {tableData?.length != null && (
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.35)", pt: "4px" }}>
              {tableData.length} rows
            </Typography>
          )}
          <IconButton
            onClick={handleDownload}
            disabled={!tableData?.length}
            size="small"
            title="Download readings as CSV"
            sx={{
              color: "rgba(255,255,255,0.45)",
              mt: "-2px",
              "&:hover": { color: "#FDB940", bgcolor: "rgba(253,185,64,0.08)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.15)" },
            }}
          >
            <FileDownloadIcon sx={{ fontSize: "1.15rem" }} />
          </IconButton>
        </Box>
      </Box>

      <DataGrid
        key={startDate ? startDate.toISOString() : "live"}
        sx={dataGridSx}
        rows={tableData ?? []}
        columns={columns}
        autoHeight
        slots={{ pagination: CustomPagination }}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25]}
        disableSelectionOnClick
        disableRowSelectionOnClick
        columnBuffer={3}
      />
    </Box>
  );
}
